import { StanMultiSelect } from '@/components';
import { normalizeTags } from '../lib/tags.logic';
import { useCustomerTags } from '../queries/useCustomerTags';
import { MAX_CUSTOM_TAG_LENGTH } from '../constants';
import { useCallback, useMemo } from 'react';
import { isAxiosError } from 'axios';
import { stanNotify } from '@/lib';
import { SENTRY_FEATURES } from '@/lib/constants';

interface CustomerTagsFieldProps {
    userFanId: number;
    disabled?: boolean;
    id?: string;
    name?: string;
}

export function CustomerTagsField({ userFanId, disabled = false, id, name }: CustomerTagsFieldProps) {
    const { customerTags, availableTags, updateTagsMutation } = useCustomerTags(userFanId, true);
    const options = useMemo(
        () => normalizeTags([...availableTags, ...customerTags]).map(tag => ({ id: tag, label: tag })),
        [availableTags, customerTags]
    );

    const handleTagsChange = useCallback(
        async (next: string[]) => {
            const normalized = normalizeTags(next);
            try {
                await updateTagsMutation.mutateAsync(normalized);
            } catch (error) {
                const message =
                    isAxiosError(error) && error.response?.data?.message
                        ? error.response.data.message
                        : 'Unable to update tags. Please try again.';

                stanNotify({
                    type: 'error',
                    title: 'Unable to update tags',
                    text: message,
                    duration: 5000,
                    error: {
                        message: '[AUDIENCE] failed-to-update-tags',
                        options: {
                            tags: { feature: SENTRY_FEATURES.AUDIENCE },
                            context: { userFanId, error },
                        },
                    },
                });
            }
        },
        [updateTagsMutation, userFanId]
    );

    return (
        <div className='flex flex-col gap-2'>
            <span className='text-dark caption-1-bold'>Tags</span>
            <StanMultiSelect
                id={id}
                name={name}
                value={customerTags}
                options={options}
                maxTags={MAX_CUSTOM_TAG_LENGTH}
                onChange={handleTagsChange}
                disabled={disabled || updateTagsMutation.isPending}
            />
        </div>
    );
}
